/**
 * Releases tab — live releases per channel and product type.
 *
 * Wires the new /api/apps/:appId/releases + /releases/:id/{rollback,bump-rollout,force-update}
 * endpoints. Shows: status badge, scope visualization, action buttons.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bumpRollout,
  createBuild,
  createRelease,
  deleteRelease,
  forceUpdate,
  getRelease,
  listApps,
  listChannels,
  listDeviceGroups,
  listProductTypes,
  listReleases,
  listReleaseApprovals,
  approveReleaseApproval,
  rejectReleaseApproval,
  publishRelease,
  rollbackRelease,
  updateRelease,
  type Release,
  type ReleaseApproval,
  type ReleaseScope,
  type ProductType,
} from "../lib/api";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectList,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  Skeleton,
} from "raft-ui";
import type { BadgeProps } from "raft-ui";
import { useToast } from "../components/Toast";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog";
import { ReleaseAssetsPanel } from "../components/ReleaseAssetsPanel";
import { ReleaseAssetUploader } from "../components/ReleaseAssetUploader";
import { ChangelogEditor, ChangelogViewer } from "../components/ChangelogMarkdown";
import { createBuildAsset, uploadApk } from "../lib/api";
import type { PendingFile } from "../lib/releaseFileDetect";


const ROLLOUT_PRESETS = [5, 25, 50, 100];

type EditableScopeType = "full" | "platform" | "user_cohort" | "ip_range" | "device_group";
type ScopeInput = { scope_type: string; scope_value: string };

function buildReleaseScopes(
  scopeType: EditableScopeType,
  scopeValue: string,
  alwaysIncludedGroupIds: string[],
): ScopeInput[] {
  if (scopeType !== "full") {
    return [{ scope_type: scopeType, scope_value: scopeValue.trim() }];
  }
  return [
    { scope_type: "full", scope_value: "all" },
    ...[...new Set(alwaysIncludedGroupIds)].sort().map((groupId) => ({
      scope_type: "device_group",
      scope_value: groupId,
    })),
  ];
}

function AlwaysIncludedDeviceGroups({
  groups,
  selectedIds,
  onChange,
}: {
  groups: { id: string; name: string; member_count: number }[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
}) {
  if (groups.length === 0) {
    return <p className="text-xs text-foreground-muted">No device groups available.</p>;
  }
  return (
    <div className="max-h-36 overflow-y-auto rounded-sm border border-line-muted divide-y divide-line-hairline">
      {groups.map((group) => {
        const checked = selectedIds.includes(group.id);
        return (
          <label key={group.id} className="flex items-center gap-2 px-2 py-1.5 text-xs">
            <Checkbox
              checked={checked}
              onCheckedChange={(value) => {
                const next = Boolean(value)
                  ? [...selectedIds, group.id]
                  : selectedIds.filter((id) => id !== group.id);
                onChange([...new Set(next)].sort());
              }}
            />
            <span className="min-w-0 flex-1 truncate">{group.name}</span>
            <span className="font-mono text-foreground-muted">{group.member_count}</span>
          </label>
        );
      })}
    </div>
  );
}

function RolloutPercentInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <span className="flex items-center gap-1">
      <Input
        type="number"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next)) onChange(Math.min(100, Math.max(0, Math.trunc(next))));
        }}
        className="w-16 text-right font-mono text-xs"
      />
      <span className="text-foreground-muted">%</span>
      {ROLLOUT_PRESETS.map((preset) => (
        <Button
          key={preset}
          type="button"
          size="xs"
          variant={value === preset ? "primary" : "outline"}
          aria-pressed={value === preset}
          onClick={() => onChange(preset)}
        >
          {preset}
        </Button>
      ))}
    </span>
  );
}

function parseJsonStringArray(value: string): string[] {
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

function productTypeMatchesPlatform(productType: ProductType, appPlatform?: string | null): boolean {
  if (!appPlatform) return true;
  const platform = appPlatform.toLowerCase();
  const supported = parseJsonStringArray(productType.supported_platforms_json)
    .map((item) => item.toLowerCase());
  if (supported.some((item) => item === platform || item.startsWith(`${platform}-`))) {
    return true;
  }
  if (platform === "android") {
    return productType.name.startsWith("android-") || productType.parser_kind === "apk-aapt";
  }
  if (platform === "ios") {
    return productType.name.startsWith("ios-") || supported.some((item) => item.includes("iphone"));
  }
  if (platform === "electron") {
    return productType.name.includes("electron") || productType.parser_kind === "electron-asar";
  }
  if (platform === "node") {
    return productType.name === "cli-binary" || productType.parser_kind === "external";
  }
  return productType.name.includes(platform) || productType.parser_kind.includes(platform);
}

export function PendingReleaseApprovals({ appId, gateOn }: { appId: string; gateOn: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const approvals = useQuery({
    queryKey: ["release-approvals", appId],
    queryFn: () => listReleaseApprovals(appId, "pending"),
  });
  const mutation = useMutation({
    mutationFn: ({ requestId, action }: { requestId: string; action: "approve" | "reject" }) =>
      action === "approve" ? approveReleaseApproval(appId, requestId) : rejectReleaseApproval(appId, requestId),
    onSuccess: (_data, vars) => {
      toast.show({
        kind: "success",
        title: vars.action === "approve" ? "Release approved and published" : "Release request rejected",
      });
      qc.invalidateQueries({ queryKey: ["release-approvals", appId] });
      qc.invalidateQueries({ queryKey: ["releases", appId] });
    },
    onError: (e) =>
      toast.show({ kind: "error", title: "Action failed", description: (e as Error).message }),
  });
  const items = approvals.data?.approvals ?? [];
  // The queue stays reachable even if the gate was switched off after requests
  // were created — pending approvals hang until decided, so they must not vanish
  // from the official entry point (task #239 review item 5).
  if (!gateOn && !approvals.isLoading && items.length === 0) return null;
  return (
    <section className="border-t border-line-hairline pt-5 mb-4">
      <h3 className="text-sm font-medium text-foreground-strong mb-2">Pending release approvals</h3>
      {approvals.isLoading ? (
        <Skeleton className="h-10" />
      ) : items.length === 0 ? (
        <div className="text-xs text-foreground-muted">No releases waiting for approval.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((a: ReleaseApproval) => (
            <li
              key={a.id}
              className="rounded-md bg-fill-muted p-2.5 flex items-start justify-between gap-3"
            >
              <div className="text-sm min-w-0">
                <div className="font-medium">
                  {a.version_name ?? "?"}{" "}
                  <span className="text-xs text-foreground-muted">
                    (code {a.version_code ?? "?"}) · {a.channel_slug ?? ""}
                  </span>
                </div>
                {a.changelog && (
                  <div className="text-xs text-foreground whitespace-pre-wrap">{a.changelog}</div>
                )}
                {a.assets.length > 0 && (
                  <ul className="text-xs text-foreground mt-1 space-y-0.5">
                    {a.assets.map((asset, i) => (
                      <li key={i} className="font-mono break-all">
                        {asset.artifact_kind} · {asset.platform}
                        {asset.arch ? `/${asset.arch}` : ""}
                        {asset.variant ? `/${asset.variant}` : ""} · {asset.filetype} ·{" "}
                        {asset.size_bytes} B · sha256 {asset.file_hash.slice(0, 16)}…
                      </li>
                    ))}
                  </ul>
                )}
                {(() => {
                  // The conditions the approver is signing off on (task #239 review).
                  // Guard the parsed values are arrays with the expected item
                  // shape — a malformed stored value must not break the card.
                  const parseScopes = (): { scope_type: string; scope_value: string }[] => {
                    try {
                      const v = JSON.parse(a.expected_scopes);
                      return Array.isArray(v)
                        ? v.filter(
                            (s) => s && typeof s.scope_type === "string" && typeof s.scope_value === "string",
                          )
                        : [];
                    } catch {
                      return [];
                    }
                  };
                  const parseTargets = (): string[] => {
                    try {
                      const v = a.required_external_targets ? JSON.parse(a.required_external_targets) : [];
                      return Array.isArray(v) ? v.filter((t) => typeof t === "string") : [];
                    } catch {
                      return [];
                    }
                  };
                  const scopes = parseScopes();
                  const targets = parseTargets();
                  return (
                    <div className="text-xs text-foreground mt-1 flex flex-wrap items-center gap-1">
                      <span className="text-foreground-muted">Publishes to</span>
                      {scopes.length === 0 ? (
                        <span className="font-mono">full:all</span>
                      ) : (
                        scopes.map((s, i) => (
                          <span key={i} className="font-mono rounded-sm bg-layer-inset px-1">
                            {s.scope_type}:{s.scope_value}
                          </span>
                        ))
                      )}
                      {targets.length > 0 && (
                        <>
                          <span className="text-foreground-muted">· requires targets</span>
                          {targets.map((t, i) => (
                            <span key={i} className="font-mono rounded-sm bg-layer-inset px-1">
                              {t}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  );
                })()}
                <div className="text-xs text-foreground-muted mt-1">
                  Requested by {a.requested_by_actor} · {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="primary"
                  className="text-sm"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ requestId: a.id, action: "approve" })}
                >
                  Approve
                </Button>
                <Button
                  className="text-sm"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ requestId: a.id, action: "reject" })}
                >
                  Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function Releases({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showNewRelease, setShowNewRelease] = useState(false);

  const app = useQuery({ queryKey: ["apps"], queryFn: () => listApps() });
  const releases = useQuery({
    queryKey: ["releases", appId],
    queryFn: () => listReleases(appId),
  });
  const channels = useQuery({
    queryKey: ["channels", appId],
    queryFn: () => listChannels(appId),
  });
  const productTypes = useQuery({
    queryKey: ["product-types", appId],
    queryFn: () => listProductTypes(appId),
  });

  const thisApp = app.data?.apps.find((a) => a.id === appId);

  // Filter client-side (expert's endpoint may or may not accept filter params;
  // we keep filtering here for v1 simplicity)
  const filtered = (releases.data?.releases ?? []).filter((r) => {
    if (channelFilter !== "all") {
      const ch = channels.data?.channels.find((c) => c.id === r.channel_id);
      if (ch?.slug !== channelFilter) return false;
    }
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    return true;
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-semibold">Releases</h2>
        <Button
          variant="primary"
          className="text-sm"
          onClick={() => setShowNewRelease(true)}
        >
          + New release
        </Button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Total" value={releases.data?.releases.length ?? 0} />
        <Stat
          label="Active"
          value={releases.data?.releases.filter((r) => r.status === "active").length ?? 0}
        />
        <Stat
          label="Draft"
          value={releases.data?.releases.filter((r) => r.status === "draft").length ?? 0}
        />
        <Stat
          label="Channels"
          value={channels.data?.channels.length ?? 0}
        />
      </div>

      {/* Release human-approval queue (task #239). Mounted regardless of the
          current toggle so already-pending requests stay reachable; the
          component hides itself only when the gate is off and nothing is pending. */}
      <PendingReleaseApprovals appId={appId} gateOn={Boolean(thisApp?.release_requires_human_approval)} />

      {/* Filters */}
      <div className="mb-4 flex flex-row flex-wrap gap-3 items-center">
        <Select
          items={{
            all: "All channels",
            ...Object.fromEntries(
              (channels.data?.channels ?? []).map((c) => [c.slug, c.slug]),
            ),
          }}
          value={channelFilter}
          onValueChange={(v) => setChannelFilter(v as string)}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
            <SelectIcon />
          </SelectTrigger>
          <SelectContent>
            <SelectList>
            <SelectItem value="all">All channels</SelectItem>
            {channels.data?.channels.map((c) => (
              <SelectItem key={c.id} value={c.slug}>
                {c.slug}
              </SelectItem>
            ))}
          </SelectList>
          </SelectContent>
        </Select>
        <Select
          items={{
            all: "All statuses",
            draft: "Draft",
            active: "Active",
            superseded: "Superseded",
            cancelled: "Cancelled",
          }}
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as string)}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
            <SelectIcon />
          </SelectTrigger>
          <SelectContent>
            <SelectList>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="superseded">Superseded</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectList>
          </SelectContent>
        </Select>
      </div>

      {releases.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}
      {releases.error && (
        <p className="text-danger">Failed: {(releases.error as Error).message}</p>
      )}

      {filtered.length === 0 && !releases.isLoading && (
        <EmptyState>
          <EmptyStateTitle>No releases match your filter.</EmptyStateTitle>
          <EmptyStateDescription>
            Create a new release and upload the required assets from this tab.
          </EmptyStateDescription>
        </EmptyState>
      )}

      <div className="space-y-6">
        {filtered.map((r) => {
          const channel = channels.data?.channels.find((c) => c.id === r.channel_id);
          const pt = productTypes.data?.product_types.find((p) => p.name === r.product_type);
          return (
            <ReleaseRow
              key={r.id}
              release={r}
              appId={appId}
              channelSlug={channel?.slug ?? "?"}
              productTypeName={pt?.display_name ?? r.product_type}
              onAction={() => {
                qc.invalidateQueries({ queryKey: ["releases", appId] });
              }}
            />
          );
        })}
      </div>

      {showNewRelease && (
        <NewReleaseDialog
          appId={appId}
          onClose={() => setShowNewRelease(false)}
          onCreated={() => {
            setShowNewRelease(false);
            qc.invalidateQueries({ queryKey: ["releases", appId] });
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-foreground-muted">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function ReleaseRow({
  release: r,
  appId,
  channelSlug,
  productTypeName,
  onAction,
}: {
  release: Release;
  appId: string;
  channelSlug: string;
  productTypeName: string;
  onAction: () => void;
}) {
  const toast = useToast();
  const [showRollout, setShowRollout] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [newPercent, setNewPercent] = useState<number>(r.rollout_cohort_count ?? 100);

  const detail = useQuery({
    queryKey: ["release-detail", r.id],
    queryFn: () => getRelease(appId, r.id),
  });
  const currentRevision = detail.data?.release.revision ?? r.revision;

  const publish = useMutation({
    mutationFn: () => {
      const scopes = detail.data?.scopes ?? [];
      if (scopes.length === 0) throw new Error("Release has no scope to publish");
      return publishRelease(
        appId,
        r.id,
        scopes.map((scope) => ({
          scope_type: scope.scope_type,
          scope_value: scope.scope_value,
        })),
        currentRevision,
      );
    },
    onSuccess: () => {
      toast.show({ kind: "success", title: `Published ${channelSlug}` });
      onAction();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Publish failed",
        description: (e as Error).message,
      }),
  });

  const rollback = useMutation({
    mutationFn: () => rollbackRelease(appId, r.id, {
      expected_revision: currentRevision,
    }),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: data.restored_to_draft
          ? `Restored ${channelSlug} draft`
          : `Restored ${channelSlug} as active`,
      });
      onAction();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Rollback failed",
        description: (e as Error).message,
      }),
  });

  const bump = useMutation({
    mutationFn: (target: number) => bumpRollout(appId, r.id, {
      to: target,
      expected_revision: currentRevision,
    }),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: `Rollout set to ${data.rollout_cohort_count ?? "?"}%`,
      });
      onAction();
      setShowRollout(false);
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Bump rollout failed",
        description: (e as Error).message,
      }),
  });

  const toggleForce = useMutation({
    mutationFn: () => forceUpdate(appId, r.id, {
      enabled: !r.should_force_update,
      expected_revision: currentRevision,
    }),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: data.should_force_update
          ? "Now forcing update on clients"
          : "No longer forcing update",
      });
      onAction();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Force-update toggle failed",
        description: (e as Error).message,
      }),
  });

  const cancel = useMutation({
    mutationFn: () => deleteRelease(appId, r.id, currentRevision),
    onSuccess: () => {
      toast.show({
        kind: "success",
        title: r.status === "draft" ? "Draft deleted" : "Release cancelled",
      });
      setConfirmCancel(false);
      onAction();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: r.status === "draft" ? "Delete failed" : "Cancel failed",
        description: (e as Error).message,
      }),
  });

  return (
    <section className="border-t border-line-hairline pt-5">
      <div className="flex items-center gap-3 flex-wrap">
        <ReleaseStatusBadge status={r.status} />
        <span className="font-medium">{channelSlug}</span>
        <span className="text-xs text-foreground-muted">{productTypeName}</span>
        {r.is_full ? (
          <Badge variant="success">full</Badge>
        ) : (
          <Badge variant="warning">scoped</Badge>
        )}
        {r.should_force_update ? (
          <Badge variant="warning">⚠ force</Badge>
        ) : null}
        {r.rollout_cohort_count != null && r.rollout_cohort_count < 100 && (
          <Badge variant="warning">
            {r.rollout_cohort_count}% rollout
          </Badge>
        )}
        <span className="text-xs text-foreground-muted ml-auto">
          {new Date(r.created_at).toISOString().slice(0, 16)}Z
        </span>
      </div>
      <div className="text-xs text-foreground-muted font-mono mt-1 truncate">
        {r.id} (build {r.build_id.slice(0, 8)}…)
      </div>
      {(r.offered_count || r.current_count || r.offered_uv || r.current_uv) ? (
        <div className="mt-1 flex items-center gap-3 text-xs text-foreground-muted">
          <Tooltip>
            <TooltipTrigger
              render={
                <span>
                  <strong className="text-foreground-strong">{r.current_uv ?? 0}</strong> on this version
                </span>
              }
            />
            <TooltipContent>
              {r.current_count ?? 0} update checks (PV)
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <span>
                  <strong className="text-foreground-strong">{r.offered_uv ?? 0}</strong> offered
                </span>
              }
            />
            <TooltipContent>
              {r.offered_count ?? 0} update offers (PV)
            </TooltipContent>
          </Tooltip>
          {r.last_checked_at ? (
            <span className="text-foreground-hint">
              last check {new Date(r.last_checked_at).toLocaleString()}
            </span>
          ) : null}
        </div>
      ) : null}
      {r.changelog && (
        <div className="mt-3 border-l-2 border-line-hairline pl-3">
          <ChangelogViewer value={r.changelog} compact />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        {r.status === "draft" && (
          <Button
            variant="primary"
            className="text-xs"
            onClick={() => publish.mutate()}
            disabled={publish.isPending || !detail.data}
          >
            {publish.isPending ? "Publishing..." : !detail.data ? "Loading scope..." : "Publish"}
          </Button>
        )}
        {(r.status === "draft" || r.status === "active") && (
          <Button
            variant="outline"
            className="text-xs"
            onClick={() => setShowEdit(true)}
          >
            Edit
          </Button>
        )}
        {r.status === "active" && (
          <>
            {Boolean(r.is_full) && (
              <Button
                variant="outline"
                className="text-xs"
                onClick={() => setShowRollout(!showRollout)}
              >
                Bump rollout
              </Button>
            )}
            <Button
              variant="outline"
              className="text-xs"
              onClick={() => toggleForce.mutate()}
              disabled={toggleForce.isPending}
            >
              {r.should_force_update ? "Unforce" : "Force"}
            </Button>
          </>
        )}
        {(r.status === "superseded" || r.status === "cancelled") && (
          <Button
            variant="outline"
            className="text-xs"
            onClick={() => rollback.mutate()}
            disabled={rollback.isPending}
          >
            {r.status === "cancelled" && r.activated_at == null
              ? "Restore draft"
              : "Restore as active"}
          </Button>
        )}
        {(r.status === "draft" || r.status === "active") && (
          <Button
            variant="danger"
            className="text-xs"
            onClick={() => setConfirmCancel(true)}
          >
            {r.status === "draft" ? "Delete draft" : "Cancel release"}
          </Button>
        )}
      </div>
      {showRollout && (
        <div className="mt-2 pt-2 border-t border-line-hairline flex items-center gap-2 text-xs">
          <label>Rollout %:</label>
          <RolloutPercentInput value={newPercent} onChange={setNewPercent} />
          <Button
            variant="primary"
            className="text-xs"
            onClick={() => bump.mutate(newPercent)}
            disabled={bump.isPending}
          >
            {bump.isPending ? "…" : "Set"}
          </Button>
        </div>
      )}
      {detail.isLoading && (
        <div className="mt-2 pt-2 border-t border-line-hairline text-xs text-foreground-muted">
          Loading release details...
        </div>
      )}
      {detail.data && (
        <div className="mt-2 pt-2 border-t border-line-hairline text-xs">
          <div className="text-foreground-muted mb-1">Build: {detail.data.build?.version_name} ({detail.data.build?.version_code})</div>
          <div className="text-foreground-muted mb-1">
            Assets: {detail.data.assets.length} · Scopes: {detail.data.scopes.length}
          </div>
          {detail.data.scopes.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-foreground-muted text-left">
                  <th className="font-normal pr-2">type</th>
                  <th className="font-normal pr-2">value</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.scopes.map((s: ReleaseScope) => (
                  <tr key={s.id}>
                    <td className="pr-2 font-mono">{s.scope_type}</td>
                    <td className="pr-2 font-mono">{s.scope_value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(detail.data.checks ?? []).length > 0 && (
            <div className="mt-2">
              <div className="text-foreground-muted mb-1">Checks (advisory)</div>
              {detail.data.checks.map((chk) => (
                <div key={chk.id} className="flex items-center gap-2 mb-0.5">
                  <span
                    className={
                      chk.verdict === "passed"
                        ? "text-success-strong font-medium"
                        : chk.verdict === "failed"
                          ? "text-danger font-medium"
                          : "text-warning font-medium"
                    }
                  >
                    {chk.verdict}
                  </span>
                  <span className="font-mono">{chk.source}</span>
                  {chk.cases_total !== null && (
                    <span className="text-foreground-muted">
                      {chk.cases_passed ?? 0}/{chk.cases_total} cases
                    </span>
                  )}
                  {chk.run_url && (
                    <a
                      href={chk.run_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-info-strong hover:underline"
                    >
                      run
                    </a>
                  )}
                  {chk.summary && (
                    <span className="text-foreground-muted truncate" title={chk.summary}>
                      {chk.summary}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Asset upload zone — always visible (not gated by showDetail) so
          users can drop files onto a release row without expanding details. */}
      <ReleaseAssetsPanel
        appId={appId}
        releaseId={r.id}
        buildId={r.build_id}
        productTypeHint={r.product_type}
      />

      {showEdit && (
        <EditReleaseDialog
          appId={appId}
          release={r}
          detail={detail.data}
          loading={detail.isLoading}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            onAction();
          }}
        />
      )}

      <ConfirmActionDialog
        open={confirmCancel}
        title={r.status === "draft" ? "Delete this draft?" : "Cancel this release?"}
        objectLabel={`${channelSlug} / ${r.product_type}`}
        objectHint={r.id.slice(0, 8)}
        body={
          r.status === "draft"
            ? "The draft will be disabled. Its build, assets, and audit history stay available, while this version becomes available for a corrected upload. No live release is affected."
            : "This release will stop being served by update checks. Its build, assets, and audit history stay available. Although the version becomes available for historical correction, devices that already received this release will not update to different bits with the same version code; publish any client update with a higher version code."
        }
        confirmLabel={r.status === "draft" ? "Delete draft" : "Cancel release"}
        confirmKind="danger"
        pending={cancel.isPending}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => cancel.mutate()}
      />
    </section>
  );
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

function ReleaseStatusBadge({ status }: { status: string }) {
  const variantMap: Record<string, BadgeVariant> = {
    draft: "information",
    active: "success",
    superseded: "muted",
    cancelled: "danger",
  };
  return <Badge variant={variantMap[status] ?? "muted"}>{status}</Badge>;
}

function EditReleaseDialog({
  appId,
  release,
  detail,
  loading,
  onClose,
  onSaved,
}: {
  appId: string;
  release: Release;
  detail?: { release: Release; scopes: ReleaseScope[] } | undefined;
  loading: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [changelog, setChangelog] = useState(release.changelog ?? "");
  const deviceGroups = useQuery({
    queryKey: ["device-groups", appId],
    queryFn: () => listDeviceGroups(appId),
  });
  const [scopeType, setScopeType] = useState<EditableScopeType>("full");
  const [scopeValue, setScopeValue] = useState("all");
  const [alwaysIncludedGroupIds, setAlwaysIncludedGroupIds] = useState<string[]>([]);
  const [shouldForceUpdate, setShouldForceUpdate] = useState(Boolean(release.should_force_update));
  const [rolloutPercent, setRolloutPercent] = useState<number>(release.rollout_cohort_count ?? 100);

  useEffect(() => {
    if (!detail) return;
    const fullScope = detail.scopes.find((scope) => scope.scope_type === "full" && scope.scope_value === "all");
    const firstScope = detail.scopes[0];
    setChangelog(detail.release.changelog ?? "");
    setShouldForceUpdate(Boolean(detail.release.should_force_update));
    setRolloutPercent(detail.release.rollout_cohort_count ?? 100);
    if (fullScope) {
      setScopeType("full");
      setScopeValue("all");
      setAlwaysIncludedGroupIds(
        detail.scopes
          .filter((scope) => scope.scope_type === "device_group")
          .map((scope) => scope.scope_value)
          .sort(),
      );
    } else if (
      firstScope &&
      (firstScope.scope_type === "full" ||
        firstScope.scope_type === "platform" ||
        firstScope.scope_type === "user_cohort" ||
        firstScope.scope_type === "ip_range" ||
        firstScope.scope_type === "device_group")
    ) {
      setScopeType(firstScope.scope_type);
      setScopeValue(firstScope.scope_value);
      setAlwaysIncludedGroupIds([]);
    }
  }, [detail]);

  const save = useMutation({
    mutationFn: () =>
      updateRelease(appId, release.id, {
        expected_revision: detail?.release.revision ?? release.revision,
        changelog: changelog.trim() || null,
        should_force_update: shouldForceUpdate,
        rollout_cohort_count:
          scopeType === "full" && rolloutPercent < 100 ? rolloutPercent : null,
        scopes: buildReleaseScopes(scopeType, scopeValue, alwaysIncludedGroupIds),
      }),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Release updated" });
      onSaved();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Update failed",
        description: (e as Error).message,
      }),
  });

  const scopeValid = scopeType === "full" || scopeValue.trim().length > 0;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl">
        <DialogHeader>
          <div>
            <DialogTitle>Edit release</DialogTitle>
            <DialogDescription className="font-mono">{release.id}</DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody>
          {loading && <p className="text-sm text-foreground-muted">Loading release details...</p>}
          <div className="space-y-3">
          <div>
            <Label>Release notes</Label>
            <ChangelogEditor value={changelog} onChange={setChangelog} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scope type</Label>
              <Select
                items={{
                  full: "Full",
                  platform: "Platform",
                  user_cohort: "User cohort",
                  ip_range: "IP range",
                  device_group: "Device group",
                }}
                value={scopeType}
                onValueChange={(v) => {
                  const next = v as typeof scopeType;
                  setScopeType(next);
                  setScopeValue(next === "full" ? "all" : "");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                  <SelectIcon />
                </SelectTrigger>
                <SelectContent>
                  <SelectList>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="platform">Platform</SelectItem>
                  <SelectItem value="user_cohort">User cohort</SelectItem>
                  <SelectItem value="ip_range">IP range</SelectItem>
                  <SelectItem value="device_group">Device group</SelectItem>
                </SelectList>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scope value</Label>
              {scopeType === "device_group" ? (
                <Select
                  items={Object.fromEntries((deviceGroups.data?.groups ?? []).map((group) => [group.id, group.name]))}
                  value={scopeValue}
                  onValueChange={(value) => setScopeValue(value as string)}
                >
                  <SelectTrigger><SelectValue placeholder="Choose group" /><SelectIcon /></SelectTrigger>
                  <SelectContent>
                    <SelectList>
                    {(deviceGroups.data?.groups ?? []).map((group) => (
                      <SelectItem key={group.id} value={group.id}>{group.name} ({group.member_count})</SelectItem>
                    ))}
                  </SelectList>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={scopeValue}
                  disabled={scopeType === "full"}
                  onChange={(e) => setScopeValue(e.target.value)}
                  placeholder={scopeType === "full" ? "all" : "android-arm64-v8a"}
                />
              )}
            </div>
          </div>
          {scopeType === "full" && (
            <div>
              <Label>Always included device groups</Label>
              <AlwaysIncludedDeviceGroups
                groups={deviceGroups.data?.groups ?? []}
                selectedIds={alwaysIncludedGroupIds}
                onChange={setAlwaysIncludedGroupIds}
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={shouldForceUpdate}
              onCheckedChange={(v) => setShouldForceUpdate(Boolean(v))}
            />
            Force update
          </label>
          {scopeType === "full" && (
            <div className="flex items-center gap-2 text-xs">
              <label className="flex-1">Rollout cohort %</label>
              <RolloutPercentInput value={rolloutPercent} onChange={setRolloutPercent} />
            </div>
          )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending || loading || !scopeValid}
          >
            {save.isPending ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// NewReleaseDialog — release-first flow (P2.5 release v2)
// =============================================================================

function NewReleaseDialog({
  appId,
  onClose,
  onCreated,
}: {
  appId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const channels = useQuery({
    queryKey: ["channels", appId],
    queryFn: () => listChannels(appId),
  });
  const productTypes = useQuery({
    queryKey: ["product-types", appId],
    queryFn: () => listProductTypes(appId),
  });
  const deviceGroups = useQuery({
    queryKey: ["device-groups", appId],
    queryFn: () => listDeviceGroups(appId),
  });

  // Pre-fill the channel with the app's default release channel (set in
  // AppDetail Settings). Falls back to first channel if no default.
  const apps = useQuery({ queryKey: ["apps"], queryFn: () => listApps() });
  const thisApp = apps.data?.apps.find((a) => a.id === appId);
  const defaultChannelSlug =
    thisApp?.default_channel_slug ??
    channels.data?.channels
      .slice()
      .sort((a, b) => a.slug.localeCompare(b.slug))[0]?.slug ??
    "";

  // ---------------- 4-step wizard state ----------------
  type Step = 1 | 2 | 3 | 4;
  const STEP_LABELS = ["Target", "Version", "Assets", "Review"] as const;
  const [step, setStep] = useState<Step>(1);

  const [channelSlug, setChannelSlug] = useState<string>("");
  const [productType, setProductType] = useState<string>("");
  const [versionName, setVersionName] = useState<string>("");
  const [versionCode, setVersionCode] = useState<string>("");
  const [changelog, setChangelog] = useState<string>("");
  const [scopeType, setScopeType] = useState<EditableScopeType>("full");
  const [scopeValue, setScopeValue] = useState<string>("all");
  const [alwaysIncludedGroupIds, setAlwaysIncludedGroupIds] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [shouldForceUpdate, setShouldForceUpdate] = useState(false);
  const [rolloutPercent, setRolloutPercent] = useState(100);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const createdReleaseIdRef = useRef<string | null>(null);

  // Compute the candidate product_types for this app's platform AFTER
  // `productType` state is declared (TDZ-safe — `productType` is read here).
  const matchingProductTypes =
    productTypes.data?.product_types.filter((p) =>
      productTypeMatchesPlatform(p, thisApp?.platform),
    ) ?? [];
  const targetProductTypes =
    matchingProductTypes.length > 0
      ? matchingProductTypes
      : productTypes.data?.product_types ?? [];
  const showProductTypePicker = targetProductTypes.length > 1;
  const selectedProductType = targetProductTypes.find((p) => p.name === productType);

  // Pre-fill channel + product_type from app defaults once data is loaded.
  const [channelInit, setChannelInit] = useState(false);
  useEffect(() => {
    if (channelInit) return;
    if (channels.data && defaultChannelSlug) {
      setChannelSlug(defaultChannelSlug);
      setChannelInit(true);
    }
  }, [channels.data, defaultChannelSlug, channelInit]);

  const [ptInit, setPtInit] = useState(false);
  useEffect(() => {
    if (ptInit) return;
    if (targetProductTypes.length > 0) {
      setProductType(targetProductTypes[0]!.name);
      setPtInit(true);
    }
  }, [targetProductTypes, ptInit]);

  // ---------------- validation ----------------
  const step1Valid =
    !!channelSlug && !!productType;
  const step2Valid =
    versionName.trim().length > 0 &&
    Number.isFinite(Number(versionCode)) &&
    Number(versionCode) > 0 &&
    (scopeType === "full" || scopeValue.trim().length > 0);

  // ---------------- submit (step 4 → draft / publish) ----------------
  const submitRelease = useMutation({
    mutationFn: async (mode: "draft" | "publish") => {
      if (!step1Valid) throw new Error("Target incomplete");
      if (!step2Valid) throw new Error("Version incomplete");
      const channel = channels.data?.channels.find(
        (c) => c.slug === channelSlug,
      );
      if (!channel) throw new Error(`channel '${channelSlug}' not found`);

      // 1. create build (status=pending)
      const build = await createBuild(appId, {
        channel_id: channel.id,
        product_type: productType,
        release_type: "stable",
        version_name: versionName.trim(),
        version_code: Number(versionCode),
        changelog: changelog.trim() || undefined,
        source: "web",
        status: "pending",
        should_force_update: shouldForceUpdate || undefined,
      });
      // 2. create draft release. Publishing is an explicit lifecycle step,
      //    which keeps the release editable while assets are queued.
      const scopes = buildReleaseScopes(scopeType, scopeValue, alwaysIncludedGroupIds);
      const release = await createRelease(appId, {
        build_id: build.id,
        status: "draft",
        scopes,
        should_force_update: shouldForceUpdate || undefined,
        rollout_cohort_count:
          scopeType === "full" && rolloutPercent < 100 ? rolloutPercent : undefined,
      });
      createdReleaseIdRef.current = release.id;
      // 3. upload + register each pending file. Failures do NOT abort — the
      //    release is already live and the user can retry from the row.
      const assetFailures: string[] = [];
      for (const slot of pendingFiles) {
        try {
          const uploaded = await uploadApk(appId, slot.file);
          await createBuildAsset(appId, build.id, {
            platform: slot.platform,
            arch: slot.arch,
            variant: slot.variant,
            filetype: slot.filetype,
            r2_key: uploaded.r2_key,
            file_hash: uploaded.file_hash,
            size_bytes: uploaded.size_bytes,
          });
        } catch (e) {
          assetFailures.push(`${slot.file.name}: ${(e as Error).message}`);
        }
      }
      if (mode === "publish") {
        const published = await publishRelease(
          appId,
          release.id,
          scopes,
          release.revision,
        );
        return { release: published, assetFailures, mode };
      }
      return { release, assetFailures, mode };
    },
    onSuccess: ({ release, assetFailures, mode }) => {
      const action = mode === "publish" ? "published" : "saved as draft";
      if (assetFailures.length === 0) {
        toast.show({
          kind: "success",
          title: `Release ${release.id.slice(0, 8)}… ${action}`,
          description: `${pendingFiles.length} asset${pendingFiles.length === 1 ? "" : "s"} attached.`,
        });
      } else {
        toast.show({
          kind: "error",
          title: `Release ${release.id.slice(0, 8)}… ${action} but ${assetFailures.length} asset(s) failed`,
          description: `${assetFailures[0]?.slice(0, 80)}… — fix from the release row.`,
        });
      }
      onCreated();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Release save failed",
        description: (e as Error).message,
      }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div>
            <DialogTitle>Draft a new release</DialogTitle>
            <DialogDescription>
              Draft the release, attach binaries, then publish — all in one flow.
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody>
        {/* Stepper header */}
        <div className="flex items-center gap-1 mb-4 text-xs">
          {STEP_LABELS.map((label, i) => {
            const idx = (i + 1) as Step;
            const active = step === idx;
            const done = step > idx;
            return (
              <div key={label} className="flex items-center gap-1">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                    active
                      ? "bg-info text-foreground-inverse"
                      : done
                        ? "bg-success text-success-foreground"
                        : "bg-layer-inset text-foreground-muted"
                  }`}
                >
                  {done ? "✓" : idx}
                </div>
                <span
                  className={
                    active
                      ? "font-medium text-foreground-strong"
                      : done
                        ? "text-success-strong"
                        : "text-foreground-muted"
                  }
                >
                  {label}
                </span>
                {i < STEP_LABELS.length - 1 && (
                  <span className="text-foreground-hint mx-1">›</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-3 min-h-[260px]">
          {/* ---------------- Step 1: Target ---------------- */}
          {step === 1 && (
            <div className="space-y-3">
              <div className={showProductTypePicker ? "grid grid-cols-2 gap-3" : "grid grid-cols-1 gap-3"}>
                <div>
                  <Label>Channel</Label>
                  <Select
                    items={{
                      "": "— pick —",
                      ...Object.fromEntries(
                        (channels.data?.channels ?? []).map((c) => [
                          c.slug,
                          c.slug,
                        ]),
                      ),
                    }}
                    value={channelSlug}
                    onValueChange={(v) => setChannelSlug(v as string)}
                  >
                    <SelectTrigger autoFocus>
                      <SelectValue />
                      <SelectIcon />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectList>
                      <SelectItem value="">— pick —</SelectItem>
                      {channels.data?.channels.map((c) => (
                        <SelectItem key={c.id} value={c.slug}>
                          {c.slug}
                        </SelectItem>
                      ))}
                    </SelectList>
                    </SelectContent>
                  </Select>
                </div>
                {showProductTypePicker && (
                  <div>
                    <Label>Package type</Label>
                    <Select
                      items={{
                        "": "— pick —",
                        ...Object.fromEntries(
                          targetProductTypes.map((p) => [
                            p.name,
                            p.display_name,
                          ]),
                        ),
                      }}
                      value={productType}
                      onValueChange={(v) => setProductType(v as string)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                        <SelectIcon />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectList>
                        <SelectItem value="">— pick —</SelectItem>
                        {targetProductTypes.map((p) => (
                          <SelectItem key={p.name} value={p.name}>
                            {p.display_name}
                          </SelectItem>
                        ))}
                      </SelectList>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {!showProductTypePicker && selectedProductType && (
                <p className="text-xs text-foreground-muted">
                  Package type is set by this app:{" "}
                  <span className="font-medium">{selectedProductType.display_name}</span>.
                </p>
              )}
              {(!channels.data || channels.data.channels.length === 0) && (
                <p className="text-xs text-warning-strong">
                  ⚠ This app has no channels yet. Create one in AppDetail → Channels first.
                </p>
              )}
            </div>
          )}

          {/* ---------------- Step 2: Version + notes ---------------- */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Version name (e.g. 1.2.3)</Label>
                  <Input
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    placeholder="1.2.3"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Version code (integer)</Label>
                  <Input
                    type="number"
                    value={versionCode}
                    onChange={(e) => setVersionCode(e.target.value)}
                    placeholder="42"
                  />
                </div>
              </div>
              <div>
                <Label>Release notes</Label>
                <ChangelogEditor
                  value={changelog}
                  onChange={setChangelog}
                  minHeight={220}
                />
              </div>
            </div>
          )}

          {/* ---------------- Step 3: Assets ---------------- */}
          {step === 3 && (
            <div className="space-y-2">
              <p className="text-xs text-foreground-muted">
                Drop one or more binaries. We auto-detect platform / arch /
                filetype from the filename and keep optional fields blank when
                they are not needed.
                <br />
                <em>Optional</em>: you can publish without binaries and attach
                them later from the release row.
              </p>
              <ReleaseAssetUploader
                variant="dialog"
                deferUpload
                appId={appId}
                buildId="__pending__"
                productTypeHint={productType || "android-apk"}
                onFilesChanged={setPendingFiles}
              />
              <p className="text-[10px] text-foreground-hint">
                Asset upload happens after the release is published. The release
                is preserved even if individual assets fail.
              </p>
            </div>
          )}

          {/* ---------------- Step 4: Review ---------------- */}
          {step === 4 && (
            <div className="space-y-3 text-sm">
              <div className="border border-line-muted rounded-sm p-3 space-y-1">
                <Row k="Channel" v={channelSlug || "—"} />
                <Row k="Product type" v={productType || "—"} />
                <Row k="Version" v={`${versionName || "—"} (${versionCode || "?"})`} />
                <Row
                  k="Scope"
                  v={
                    scopeType === "full"
                      ? `full (${rolloutPercent}%)${alwaysIncludedGroupIds.length > 0
                        ? `; always ${alwaysIncludedGroupIds.map((groupId) =>
                          deviceGroups.data?.groups.find((group) => group.id === groupId)?.name ?? groupId
                        ).join(", ")}`
                        : ""}`
                      : `${scopeType}: ${scopeValue || "?"}`
                  }
                />
                <Row k="Assets" v={`${pendingFiles.length} file(s) queued`} />
                {changelog && (
                  <details className="pt-2 border-t border-line-hairline">
                    <summary className="cursor-pointer text-xs text-foreground-muted">
                      Release notes
                    </summary>
                    <div className="mt-2 rounded-md bg-layer-canvas-muted p-3">
                      <ChangelogViewer value={changelog} />
                    </div>
                  </details>
                )}
              </div>

              <details>
                <summary
                  className="cursor-pointer text-xs text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAdvanced((v) => !v);
                  }}
                >
                  {showAdvanced ? "▾" : "▸"} Advanced options (scope, force update, rollout %)
                </summary>
                {showAdvanced && (
                  <div className="mt-2 p-3 border border-line-muted rounded-sm space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Release scope</Label>
                        <Select
                          items={{ full: "Full", platform: "Platform", user_cohort: "User cohort", ip_range: "IP range", device_group: "Device group" }}
                          value={scopeType}
                          onValueChange={(value) => {
                            const next = value as typeof scopeType;
                            setScopeType(next);
                            setScopeValue(next === "full" ? "all" : "");
                          }}
                        >
                          <SelectTrigger><SelectValue /><SelectIcon /></SelectTrigger>
                          <SelectContent>
                            <SelectList>
                            <SelectItem value="full">Full</SelectItem>
                            <SelectItem value="platform">Platform</SelectItem>
                            <SelectItem value="user_cohort">User cohort</SelectItem>
                            <SelectItem value="ip_range">IP range</SelectItem>
                            <SelectItem value="device_group">Device group</SelectItem>
                          </SelectList>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Scope value</Label>
                        {scopeType === "device_group" ? (
                          <Select
                            items={Object.fromEntries((deviceGroups.data?.groups ?? []).map((group) => [group.id, group.name]))}
                            value={scopeValue}
                            onValueChange={(value) => setScopeValue(value as string)}
                          >
                            <SelectTrigger><SelectValue placeholder="Choose group" /><SelectIcon /></SelectTrigger>
                            <SelectContent>
                              <SelectList>
                              {(deviceGroups.data?.groups ?? []).map((group) => (
                                <SelectItem key={group.id} value={group.id}>{group.name} ({group.member_count})</SelectItem>
                              ))}
                            </SelectList>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={scopeValue}
                            disabled={scopeType === "full"}
                            onChange={(event) => setScopeValue(event.target.value)}
                            placeholder={scopeType === "full" ? "all" : "android-arm64-v8a"}
                          />
                        )}
                      </div>
                    </div>
                    {scopeType === "full" && (
                      <div>
                        <Label>Always included device groups</Label>
                        <AlwaysIncludedDeviceGroups
                          groups={deviceGroups.data?.groups ?? []}
                          selectedIds={alwaysIncludedGroupIds}
                          onChange={setAlwaysIncludedGroupIds}
                        />
                      </div>
                    )}
                    <label className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={shouldForceUpdate}
                        onCheckedChange={(v) => setShouldForceUpdate(Boolean(v))}
                      />
                      Force update — clients must upgrade on next launch
                    </label>
                    {scopeType === "full" && (
                      <div className="flex items-center gap-2 text-xs">
                        <label className="flex-1">Rollout cohort %</label>
                        <RolloutPercentInput
                          value={rolloutPercent}
                          onChange={setRolloutPercent}
                        />
                      </div>
                    )}
                  </div>
                )}
              </details>

              <p className="text-xs text-foreground-muted pt-2 border-t border-line-hairline">
                Publishing creates the build, the release row, and uploads all
                queued assets in order. Asset failures don't roll back the release.
              </p>
            </div>
          )}
        </div>

        </DialogBody>
        {/* Wizard nav buttons */}
        <DialogFooter className="justify-between">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((step - 1) as Step)}
                disabled={submitRelease.isPending}
              >
                Back
              </Button>
            )}
            {step < 4 && (
              <Button
                type="button"
                variant="primary"
                disabled={
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid)
                }
                onClick={() => setStep((step + 1) as Step)}
              >
                Next
              </Button>
            )}
            {step === 4 && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitRelease.isPending || !step1Valid || !step2Valid}
                  onClick={() => submitRelease.mutate("draft")}
                >
                  {submitRelease.isPending ? "Saving..." : "Save draft"}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={submitRelease.isPending || !step1Valid || !step2Valid}
                  onClick={() => submitRelease.mutate("publish")}
                >
                  {submitRelease.isPending ? "Publishing..." : "Publish now"}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 text-xs text-foreground-muted">{k}</div>
      <div className="font-mono text-xs">{v}</div>
    </div>
  );
}
